import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import ProductBottle from '../../../components/feature/ProductBottle';
import { useSEO, generateProductSchema, generateOrganizationSchema } from '../../../utils/seo';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { allProducts } from '../../../mocks/products';

export default function BPC157LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedVariant, setSelectedVariant] = useState<'5mg' | '10mg' | '50mg'>('10mg');
  const [quantity, setQuantity] = useState(1);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [notes, setNotes] = useState('');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  // BPC-157 variants from product catalog
  const bpc157Variants = {
    '5mg': allProducts.find(p => p.id === 'bpc-157-5mg')!,
    '10mg': allProducts.find(p => p.id === 'bpc-157-10mg')!,
    '50mg': allProducts.find(p => p.id === 'bpc-157-50mg')!,
  };

  const currentProduct = bpc157Variants[selectedVariant];

  // SEO Configuration
  useSEO({
    title: 'BPC-157 Research Peptide | COA-Backed Laboratory Supply | DN Biotech',
    description: 'BPC-157 research peptide with 99%+ purity, HPLC verification, and Certificate of Analysis. Laboratory-grade supply for qualified researchers. Quote-based B2B ordering.',
    keywords: 'BPC-157 research peptide, BPC-157 COA, BPC-157 laboratory supplier, BPC-157 HPLC, research grade BPC-157, peptide supplier',
    canonical: `${siteUrl}/product/bpc-157`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(siteUrl),
        generateProductSchema(siteUrl, currentProduct),
        {
          '@type': 'WebPage',
          name: 'BPC-157 Research Peptide - Laboratory Supply',
          description: 'Comprehensive information about BPC-157 research peptide for laboratory use. COA-backed, HPLC verified, 99%+ purity.',
          url: `${siteUrl}/product/bpc-157`,
        }
      ]
    }
  });

  const handleRequestQuote = () => {
    if (!user) {
      navigate('/login', { state: { from: '/product/bpc-157' } });
      return;
    }
    setShowQuoteForm(true);
  };

  const handleSubmitQuote = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    setFormStatus('submitting');

    try {
      const { error } = await supabase
        .from('quote_requests')
        .insert({
          user_id: user.id,
          product_name: currentProduct.name,
          dosage: currentProduct.specification,
          quantity: quantity,
          notes: notes || null,
          status: 'pending'
        });

      if (error) throw error;

      setFormStatus('success');
      setNotes('');
      
      setTimeout(() => {
        setShowQuoteForm(false);
        setFormStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const researchApplications = [
    {
      title: 'Tissue Repair Mechanisms',
      description: 'Investigated in preclinical models for potential effects on cellular repair pathways and wound healing processes.',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Gastrointestinal Research',
      description: 'Studied for its origins in gastric juice and potential protective effects on GI tissue in laboratory settings.',
      icon: 'ri-flask-line'
    },
    {
      title: 'Musculoskeletal Studies',
      description: 'Examined in cell and animal models for potential effects on tendon, ligament, and muscle tissue.',
      icon: 'ri-body-scan-line'
    },
    {
      title: 'Angiogenesis Research',
      description: 'Investigated for potential effects on blood vessel formation and vascular repair mechanisms.',
      icon: 'ri-pulse-line'
    }
  ];

  const qualityFeatures = [
    {
      title: 'Certificate of Analysis',
      description: 'Every batch includes comprehensive COA with purity results, molecular weight confirmation, and testing methodology.',
      icon: 'ri-file-list-3-line'
    },
    {
      title: 'HPLC Verification',
      description: 'High-Performance Liquid Chromatography analysis confirms 99%+ purity for each production batch.',
      icon: 'ri-test-tube-line'
    },
    {
      title: 'Mass Spectrometry',
      description: 'MS data confirms molecular identity and structural integrity of the peptide compound.',
      icon: 'ri-microscope-line'
    },
    {
      title: 'Third-Party Testing',
      description: 'Independent laboratory verification ensures unbiased quality assessment and documentation.',
      icon: 'ri-shield-check-line'
    }
  ];

  const whyChooseUs = [
    {
      title: 'B2B Research Focus',
      description: 'Dedicated to serving qualified researchers, laboratories, and academic institutions with professional-grade compounds.',
      icon: 'ri-building-2-line'
    },
    {
      title: 'Complete Documentation',
      description: 'Full traceability with batch numbers, COAs, and testing records for regulatory compliance.',
      icon: 'ri-folder-shield-line'
    },
    {
      title: 'Consistent Supply',
      description: 'Reliable inventory management ensures consistent availability for ongoing research programs.',
      icon: 'ri-refresh-line'
    },
    {
      title: 'Technical Support',
      description: 'Knowledgeable team available to assist with product specifications and documentation requirements.',
      icon: 'ri-customer-service-2-line'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-sm mb-8">
            <Link to="/shop" className="text-gray-400 hover:text-teal-400 cursor-pointer transition-colors">
              Shop
            </Link>
            <i className="ri-arrow-right-s-line text-gray-500"></i>
            <Link to="/category/recovery-repair" className="text-gray-400 hover:text-teal-400 cursor-pointer transition-colors">
              Recovery & Repair
            </Link>
            <i className="ri-arrow-right-s-line text-gray-500"></i>
            <span className="text-teal-400 font-medium">BPC-157</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Product Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-3xl p-12 shadow-2xl">
                <div className="flex items-center justify-center h-80">
                  <ProductBottle
                    productName="BPC-157"
                    specification={currentProduct.specification}
                    purity={currentProduct.purity}
                    batchNumber="DB-BPC157"
                    className="transform scale-150"
                  />
                </div>
                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center space-x-6">
                  <div className="flex items-center space-x-2 text-gray-300">
                    <i className="ri-shield-check-line text-teal-400"></i>
                    <span className="text-sm">99%+ Purity</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <i className="ri-file-list-3-line text-teal-400"></i>
                    <span className="text-sm">COA Included</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <i className="ri-test-tube-line text-teal-400"></i>
                    <span className="text-sm">HPLC Verified</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Content */}
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/20 text-teal-400 text-sm font-semibold rounded-full mb-6">
                <i className="ri-flask-line mr-2"></i>
                Research Grade Peptide
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                BPC-157 Research Peptide – COA-Backed Laboratory Supply
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                High-purity pentadecapeptide for qualified laboratory research. Every batch verified through HPLC analysis with comprehensive Certificate of Analysis documentation.
              </p>

              {/* Variant Selector */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-300 mb-3">Select Specification</label>
                <div className="flex flex-wrap gap-3">
                  {(['5mg', '10mg', '50mg'] as const).map((variant) => (
                    <button
                      key={variant}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-6 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        selectedVariant === variant
                          ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30'
                          : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                      }`}
                    >
                      {variant} vial
                    </button>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={handleRequestQuote}
                  className="flex-1 px-8 py-4 bg-teal-500 text-white text-lg font-semibold rounded-xl hover:bg-teal-600 transition-all shadow-lg shadow-teal-500/30 cursor-pointer whitespace-nowrap flex items-center justify-center"
                >
                  <i className="ri-mail-send-line mr-2"></i>
                  Request Quote
                </button>
                <a
                  href="https://wa.me/639178850200"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-xl hover:bg-green-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp Inquiry
                </a>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Purity</p>
                  <p className="text-white font-semibold">{currentProduct.purity}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Testing</p>
                  <p className="text-white font-semibold">HPLC + MS</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Lead Time</p>
                  <p className="text-white font-semibold">5-10 Days</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">MOQ</p>
                  <p className="text-white font-semibold">1 Box</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Use Disclaimer */}
      <section className="bg-red-50 border-y border-red-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center space-x-3">
            <i className="ri-alert-line text-red-600 text-xl flex-shrink-0"></i>
            <p className="text-sm font-semibold text-red-900 text-center">
              For Research Use Only. Not for human consumption, medical use, veterinary use, or household application.
            </p>
          </div>
        </div>
      </section>

      {/* What is BPC-157 Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                What is BPC-157?
              </h2>
              <div className="prose prose-lg text-gray-600">
                <p className="mb-4 leading-relaxed">
                  <strong>BPC-157</strong> (Body Protection Compound-157) is a synthetic pentadecapeptide consisting of 15 amino acids. It is derived from a protective protein found naturally in human gastric juice and has been the subject of extensive preclinical research.
                </p>
                <p className="mb-4 leading-relaxed">
                  The peptide sequence (Gly-Glu-Pro-Pro-Pro-Gly-Lys-Pro-Ala-Asp-Asp-Ala-Gly-Leu-Val) has been investigated in numerous cell culture and animal model studies for its potential biological activities.
                </p>
                <p className="leading-relaxed">
                  BPC-157 is classified as a research compound and is supplied exclusively for laboratory investigation by qualified researchers and institutions.
                </p>
              </div>
              
              {/* Molecular Info Box */}
              <div className="mt-8 bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-4">Molecular Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Molecular Formula</p>
                    <p className="font-medium text-slate-900">C₆₂H₉₈N₁₆O₂₂</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Molecular Weight</p>
                    <p className="font-medium text-slate-900">1419.53 g/mol</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Sequence Length</p>
                    <p className="font-medium text-slate-900">15 Amino Acids</p>
                  </div>
                  <div>
                    <p className="text-gray-500">CAS Number</p>
                    <p className="font-medium text-slate-900">137525-51-0</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/cf33bd384e2d60e01efb7c6d88e5b11a.jpg"
                alt="Laboratory research environment"
                className="rounded-2xl shadow-xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-teal-500 text-white rounded-xl p-6 shadow-lg">
                <p className="text-3xl font-bold">500+</p>
                <p className="text-sm opacity-90">Published Studies</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Applications Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Laboratory Research Applications
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              BPC-157 has been investigated in various preclinical research contexts. The following represents areas of published scientific inquiry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {researchApplications.map((app, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-6">
                  <i className={`${app.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{app.title}</h3>
                <p className="text-gray-600 leading-relaxed">{app.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <i className="ri-information-line text-amber-600 text-2xl flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="font-semibold text-amber-900 mb-2">Research Context Disclaimer</h4>
                <p className="text-sm text-amber-800 leading-relaxed">
                  The research applications listed above are based on published preclinical studies conducted in cell cultures and animal models. These findings do not constitute medical claims and should not be interpreted as evidence of efficacy for any human condition. BPC-157 is supplied exclusively for laboratory research purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Verification Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Quality & Verification Standards
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Every batch of BPC-157 undergoes rigorous testing and documentation to ensure research-grade quality and full traceability.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityFeatures.map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500 rounded-lg mb-4">
                  <i className={`${feature.icon} text-xl text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* COA Preview */}
          <div className="mt-16 bg-slate-900 rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Certificate of Analysis Included
                </h3>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Every order includes a comprehensive Certificate of Analysis documenting purity, identity, and quality testing results. Our COAs provide the documentation required for regulatory compliance and research reproducibility.
                </p>
                <ul className="space-y-3 mb-8">
                  {['HPLC purity analysis results', 'Mass spectrometry confirmation', 'Batch number and production date', 'Storage and handling specifications', 'Testing laboratory information'].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-300">
                      <i className="ri-checkbox-circle-line text-teal-400 mr-3"></i>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/quality"
                  className="inline-flex items-center px-6 py-3 bg-teal-500 text-white font-semibold rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Learn About Our Testing
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-xl">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <img
                      src="https://static.readdy.ai/image/f774deb3335ef344be2f4793f6ebd557/d3524caf242f33efd937ef8bb569492a.png"
                      alt="DN Biotech"
                      className="h-8 w-auto"
                    />
                    <span className="font-bold text-slate-900">Certificate of Analysis</span>
                  </div>
                  <span className="text-sm text-gray-500">Sample</span>
                </div>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Product Name</span>
                    <span className="font-medium text-slate-900">BPC-157</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Batch Number</span>
                    <span className="font-medium text-slate-900">DB-BPC157-XXXX</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Purity (HPLC)</span>
                    <span className="font-medium text-teal-600">≥99.0%</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Molecular Weight</span>
                    <span className="font-medium text-slate-900">1419.53 g/mol</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Appearance</span>
                    <span className="font-medium text-slate-900">White Powder</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Testing Method</span>
                    <span className="font-medium text-slate-900">HPLC, MS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Why Choose DN Biotech for BPC-157
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We specialize in providing research-grade peptides to qualified laboratories and institutions with professional service and documentation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
                  <i className={`${item.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Variants & Ordering Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Available Specifications
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              BPC-157 is available in multiple specifications to accommodate various research requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {(['5mg', '10mg', '50mg'] as const).map((variant) => {
              const product = bpc157Variants[variant];
              const isSelected = selectedVariant === variant;
              return (
                <div
                  key={variant}
                  className={`relative rounded-2xl p-8 border-2 transition-all cursor-pointer ${
                    isSelected
                      ? 'border-teal-500 bg-teal-50 shadow-lg'
                      : 'border-gray-200 bg-white hover:border-teal-300'
                  }`}
                  onClick={() => setSelectedVariant(variant)}
                >
                  {variant === '10mg' && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="px-4 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">{product.specification}</h3>
                    <p className="text-gray-600 mb-4">10 vials per box</p>
                    <div className="flex items-center justify-center space-x-2 mb-6">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                        {product.purity} Purity
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVariant(variant);
                        handleRequestQuote();
                      }}
                      className={`w-full py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                        isSelected
                          ? 'bg-teal-500 text-white hover:bg-teal-600'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      Request Quote
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* How to Order */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  How to Order BPC-157
                </h3>
                <p className="text-gray-300 mb-8 leading-relaxed">
                  All orders are processed through our quote-based system to ensure proper documentation and compliance verification for research compounds.
                </p>
                <div className="space-y-6">
                  {[
                    { step: '1', title: 'Submit Quote Request', desc: 'Select your specification and submit a quote request through our secure form.' },
                    { step: '2', title: 'Receive Quote', desc: 'Our team will review your request and provide a detailed quote within 24 hours.' },
                    { step: '3', title: 'Confirm & Pay', desc: 'Review and confirm your quote, then complete payment through your preferred method.' },
                    { step: '4', title: 'Receive Shipment', desc: 'Your order ships with full documentation including COA and tracking information.' }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-full flex-shrink-0">
                        <span className="text-white font-bold">{item.step}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center lg:text-left">
                <div className="bg-white/10 backdrop-blur rounded-xl p-8">
                  <h4 className="text-xl font-bold text-white mb-4">Ready to Order?</h4>
                  <p className="text-gray-300 mb-6">
                    Submit your quote request now and receive a response within 24 hours.
                  </p>
                  <button
                    onClick={handleRequestQuote}
                    className="w-full px-8 py-4 bg-teal-500 text-white text-lg font-semibold rounded-xl hover:bg-teal-600 transition-all cursor-pointer whitespace-nowrap mb-4"
                  >
                    <i className="ri-mail-send-line mr-2"></i>
                    Request Quote Now
                  </button>
                  <a
                    href="https://wa.me/639178850200"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-xl hover:bg-green-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp Inquiry
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="py-16 bg-red-50 border-y border-red-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-6">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl flex-shrink-0">
              <i className="ri-alert-line text-3xl text-red-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-red-900 mb-4">Important Compliance Notice</h3>
              <div className="space-y-3 text-sm text-red-800">
                <p className="leading-relaxed">
                  <strong>For Research Use Only.</strong> BPC-157 is intended solely for laboratory research purposes by qualified researchers and institutions. It is not for human consumption, medical use, veterinary use, or household application.
                </p>
                <p className="leading-relaxed">
                  This product is not intended to diagnose, treat, cure, or prevent any disease or medical condition. No medical claims are made or implied. All product information is provided for educational and research purposes only.
                </p>
                <p className="leading-relaxed">
                  Researchers must handle this product with appropriate safety protocols and comply with all applicable regulations. By requesting a quote, you confirm that you are a qualified researcher purchasing for legitimate laboratory research purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Related Research Compounds
            </h2>
            <p className="text-lg text-gray-600">
              Explore other peptides commonly used in conjunction with BPC-157 research.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              allProducts.find(p => p.id === 'tb-500-5mg')!,
              allProducts.find(p => p.id === 'bpc-tb-10mg')!,
              allProducts.find(p => p.id === 'ghk-cu-50mg')!
            ].map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.slug}`}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
              >
                <div className="relative w-full aspect-square overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{product.specification}</p>
                  <div className="flex items-center justify-between">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                      {product.purity}
                    </span>
                    <span className="text-teal-600 font-medium text-sm group-hover:underline">
                      View Details →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Request Modal */}
      {showQuoteForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://static.readdy.ai/image/f774deb3335ef344be2f4793f6ebd557/d3524caf242f33efd937ef8bb569492a.png"
                    alt="DN Biotech"
                    className="h-6 w-auto"
                  />
                  <h2 className="text-2xl font-bold text-slate-900">Request Quote</h2>
                </div>
                <button
                  onClick={() => setShowQuoteForm(false)}
                  className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 flex items-center">
                    <i className="ri-checkbox-circle-line text-xl mr-2"></i>
                    Quote request submitted successfully! You can track it in your account dashboard.
                  </p>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 flex items-center">
                    <i className="ri-error-warning-line text-xl mr-2"></i>
                    Failed to submit request. Please try again.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmitQuote} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Product Name
                  </label>
                  <input
                    type="text"
                    value={currentProduct.name}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Specification
                  </label>
                  <select
                    value={selectedVariant}
                    onChange={(e) => setSelectedVariant(e.target.value as '5mg' | '10mg' | '50mg')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="5mg">5mg vial</option>
                    <option value="10mg">10mg vial</option>
                    <option value="50mg">50mg vial</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Quantity (boxes) *
                  </label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Notes / Special Requests
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    placeholder="Any specific requirements or questions..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-8 py-4 bg-teal-500 text-white text-lg font-semibold rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Quote Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
